import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Lock, Download, FileText, ShieldCheck, MessageCircle } from "lucide-react";
import { getPaymentStatus, API } from "@/lib/api";
import { WHATSAPP_URL } from "@/lib/pdf";
import { toast } from "sonner";

/**
 * QR-target page. Looks up payment status for a will_id and either offers
 * downloads (when paid) or surfaces a clear locked message. All downloads
 * stay server-gated behind the existing payment_status check.
 */
export default function DownloadByLink() {
  const { willId } = useParams();
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!willId) return;
    getPaymentStatus(willId)
      .then(setStatus)
      .catch((e) =>
        setErr(e?.response?.status === 404 ? "Will not found." : "Could not load.")
      );
  }, [willId]);

  const downloadFile = async (fmt) => {
    setDownloading(fmt);
    try {
      const res = await fetch(`${API}/wills/${willId}/download/${fmt}`, {
        cache: "no-store",
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`;
        throw new Error(detail);
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
      const filename = m ? decodeURIComponent(m[1]) : `WillSeva-Will.${fmt}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error(e?.message || "Download failed.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="bg-[#F9FAFB] py-12 sm:py-16" data-testid="download-by-link-page">
      <div className="mx-auto max-w-xl px-5 sm:px-8">
        <div className="text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
            Secure Download
          </div>
          <h1 className="mt-2 font-serif text-3xl text-navy sm:text-4xl">
            Your WillSeva Document
          </h1>
          <p className="mt-3 text-sm text-[#4A5568]">
            Reference{" "}
            <span className="font-mono text-navy">
              WSV-{(willId || "").slice(0, 8).toUpperCase()}
            </span>
          </p>
        </div>

        <Card className="mt-8 border-[#0A1128]/10 shadow-sm">
          <CardContent className="p-6 sm:p-8">
            {err && (
              <div className="text-sm text-red-700" data-testid="download-by-link-error">
                {err}
              </div>
            )}

            {!err && !status && (
              <div className="text-sm text-[#4A5568]">Checking payment status…</div>
            )}

            {status && !status.paid && (
              <div className="space-y-4" data-testid="download-by-link-locked">
                <div className="flex items-center gap-2 text-navy">
                  <Lock size={18} className="text-gold" />
                  <div className="font-serif text-lg">Downloads are locked</div>
                </div>
                <p className="text-sm text-[#4A5568]">
                  This will hasn't been paid for yet. Complete the secure checkout
                  on the device where you started the draft, then refresh this page.
                </p>
                <Link to="/will-generator">
                  <Button
                    variant="outline"
                    className="border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
                    data-testid="download-by-link-go-back"
                  >
                    Return to Will Generator
                  </Button>
                </Link>
              </div>
            )}

            {status && status.paid && (
              <div className="space-y-5" data-testid="download-by-link-ready">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={18} className="text-emerald-600" />
                  <div className="font-serif text-lg text-navy">Ready to download</div>
                </div>
                <p className="text-xs text-[#4A5568]">
                  Payment ID{" "}
                  <span className="font-mono text-navy">
                    {status.razorpay_payment_id}
                  </span>
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Button
                    onClick={() => downloadFile("pdf")}
                    disabled={downloading === "pdf"}
                    className="bg-navy text-white hover:bg-[#1A2642]"
                    data-testid="download-by-link-pdf"
                  >
                    <Download size={16} className="mr-2" />
                    {downloading === "pdf" ? "Preparing…" : "Download PDF"}
                  </Button>
                  <Button
                    onClick={() => downloadFile("docx")}
                    disabled={downloading === "docx"}
                    variant="outline"
                    className="border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
                    data-testid="download-by-link-docx"
                  >
                    <FileText size={16} className="mr-2" />
                    {downloading === "docx" ? "Preparing…" : "Download DOCX"}
                  </Button>
                </div>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-navy underline"
                >
                  <MessageCircle size={12} /> Need help? Message us on WhatsApp
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
